import {
  joinSession,
  subscribeToParticipants,
  type NetworkQualityChange,
  type Participant,
  type ZoomMediaStream,
} from '@fireflyhealth/zoom-web'
import isEqual from 'lodash/isEqual'
import React, { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { logger } from '~/shared/lib/logger'
import { useMount, useUnmount } from '~/shared/hooks/useMount'
import { useZoomSession } from './SessionProvider'
import { SessionVideoContainer } from './SessionVideoContainer'
import { useCallTelemetry } from './useCallTelemetry'
import { useZoomEventHelp } from './zoomEventInformation'
import invariant from 'tiny-invariant'
import { fireAndForget } from '~/shared/lib/fireAndForget'

/**
 * The teardown still settling from a previous mount, keyed by client.
 *
 * `createClient()` returns a page-singleton, so closing and reopening the visit unmounts this
 * component (firing cleanup: stop media, then `client.leave()`) and remounts it (firing
 * `client.join()`) on the same client. Joining while that teardown is in flight races the SDK.
 * This lets the next mount's join await the previous mount's whole teardown, spanning remounts and
 * keyed to the client rather than a module-global flag.
 */
const pendingLeave = new WeakMap<object, Promise<unknown>>()

/**
 * SessionCallV2 Component
 *
 * Main component for managing Zoom video call sessions within the CRM appointment system.
 * Handles the complete lifecycle of a Zoom session including initialization, participant management,
 * network quality monitoring, and cleanup. The SDK-owning parts — client lifecycle, join,
 * leave/teardown and the participant-event subscription — run through `@fireflyhealth/zoom-web`.
 */
export const SessionCallV2: React.FC = () => {
  const me = useSelector(state => state.me)

  const { appointment, client, sessionName, token, isVideoOn, setIsVideoOn, setScreen } =
    useZoomSession()
  const appointmentId = appointment.id
  const appointmentMemberId = appointment.patient?.toString()

  // Core Zoom session state management
  const [stream, setStream] = useState<ZoomMediaStream | null>(null) // Zoom media stream instance
  const [currentUser, setCurrentUser] = useState<Participant | null>(null) // Current user participant info
  const [allSessionParticipants, setAllSessionParticipants] = useState<Participant[]>([]) // Full roster
  const [isInitialized, setIsInitialized] = useState(false) // Session initialization status

  // Network quality tracking for all participants, keyed by Zoom user id.
  const [networkQuality, setNetworkQuality] = useState<
    Record<number, { uplink?: number; downlink?: number }>
  >({})

  const callTelemetry = useCallTelemetry(client, appointment.id)

  // Event handling utility for Zoom events
  const { handleEvent } = useZoomEventHelp({
    currentUserId: currentUser?.userId,
  })

  /**
   * Comprehensive Zoom session cleanup handler
   *
   * Clears local media, then leaves the Zoom session. The leave is a direct `client.leave()`
   * rather than the shared core's `leaveSession`: that helper marks the client and consumes the
   * mark in `classifySessionClose`, which this consumer never calls, so the mark would never
   * clear and a later visit on the same page-singleton client would skip its leave. Leaving twice
   * (once from the button handler, once from the unmount it triggers) is harmless — the SDK fails
   * the second leave, which the catch below absorbs at warn level so an expected outcome does not
   * read as an error.
   *
   * The whole teardown is recorded in `pendingLeave` synchronously, before the first await, so a
   * remount's join awaits it rather than racing it — including during the media-teardown window
   * ahead of `client.leave()`, since the teardown is recorded as the very first statement of
   * cleanup.
   */
  const handleCleanup = useCallback(async () => {
    logger.info('Cleaning up Zoom Session')

    // Record the in-flight teardown before any await (see above). Resolved in `finally` once the
    // leave settles; a resolve-only promise, so a waiting join never inherits a teardown failure.
    let cleanupDone: () => void = () => {}
    pendingLeave.set(client, new Promise<void>(resolve => (cleanupDone = resolve)))

    callTelemetry.stop()
    try {
      /**
       * Critical: Reset state before nulling stream to prevent re-initialization
       */
      setStream(null)
      setIsInitialized(false)

      /**
       * Graceful media cleanup with error suppression
       * These operations may fail if streams are already stopped, but we want
       * to ensure the leave call always executes regardless of media state
       */
      await Promise.all([
        currentUser ? stream?.detachVideo(currentUser?.userId).catch(() => null) : null,
        stream?.stopVideo().catch(() => null),
        stream?.stopAudio().catch(() => null),
      ])
      setCurrentUser(null)
      await client.leave()
    } catch (e) {
      // Absorbed, not rethrown: the second leave of a normal teardown is expected to fail (see
      // above), and there is nothing a caller could do about a failed teardown anyway.
      logger.warn('handleCleanup', e)
    } finally {
      cleanupDone()
      logger.info('Zoom Session clean up complete')
    }
  }, [callTelemetry, client, currentUser, stream])

  /**
   * User-initiated call termination handler
   *
   * Handles the complete flow when a user chooses to leave the call:
   * 1. Performs full session cleanup
   * 2. Returns user to pre-call screen
   *
   * @returns Promise that resolves when leave process is complete
   */
  const handleLeaveCall = useCallback(async () => {
    logger.info('Leaving Zoom Session')
    await handleCleanup()
    setScreen('precall')
    logger.info('Left Zoom Session')
  }, [handleCleanup, setScreen])

  /**
   * Safety mechanism: Auto-cleanup if appointment becomes invalid
   * Prevents orphaned sessions when appointment data is corrupted or missing
   */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- tears down the external Zoom session; its state resets must precede the first await
    if (!appointmentId || !appointmentMemberId) void handleCleanup()
  }, [appointmentId, appointmentMemberId, handleCleanup])

  /**
   * Participant change handler
   *
   * An effect event, so the shared subscription is set up once per client rather than
   * re-subscribed whenever `currentUser` changes. Refreshes the current user and stores the
   * full roster; the spotlight/thumbnail split is derived downstream via `selectLayout`.
   */
  const handleRoster = useEffectEvent((updatedParticipants: Participant[]) => {
    // Guard: Ensure current user is properly initialized
    if (currentUser?.userId == null) return

    // Find and update current user info if it has changed
    const refreshedCurrentUserInfo = updatedParticipants.find(
      user => user.userId === currentUser.userId,
    )

    // Update current user state if video status or other properties changed
    if (
      refreshedCurrentUserInfo?.bVideoOn != null &&
      !isEqual(currentUser, refreshedCurrentUserInfo)
    ) {
      setCurrentUser(refreshedCurrentUserInfo)
    }

    setAllSessionParticipants(updatedParticipants)
  })

  useEffect(
    () => subscribeToParticipants(client, participants => handleRoster(participants)),
    [client],
  )

  /**
   * Zoom session initialization handler
   *
   * Joins the Zoom session with user and appointment credentials, then reads the media stream
   * and current user off the client.
   *
   * @returns Promise that resolves when initialization is complete
   * @throws Error if initialization fails at any step
   */
  const handleInitStream = useCallback(async () => {
    logger.info('Initializing Zoom Session')

    // Throws if this runs before the `me` slice has loaded.
    invariant(me.id !== null, 'Expected me to be not null')

    // Wait out any teardown still settling from a previous mount on this singleton client, so
    // join does not race it.
    await pendingLeave.get(client)

    const telemetry = callTelemetry.start()
    try {
      // The idle timeout is the minutes a session with one participant and no cloud recording
      // idles before ending: 10 replaces the SDK's 40-minute default and matches the member app.
      await joinSession(client, {
        sessionName,
        token,
        userName: me.id.toString(),
        sessionPassword: appointment.id.toString(),
        sessionIdleTimeoutMins: 10,
      })
    } catch (error) {
      telemetry.joinFailed(error)
      throw error
    }
    telemetry.joined()

    // Initialize core session objects after successful join
    // Get the media stream instance for video/audio operations
    setStream(client.getMediaStream())
    // Get current user info including video/audio state
    setCurrentUser(client.getCurrentUserInfo())
    // Mark session as fully initialized and ready for video operations
    setIsInitialized(true)

    logger.info('Zoom Session Initialized')
  }, [callTelemetry, client, sessionName, token, me.id, appointment.id])

  /**
   * Derived network quality calculator
   *
   * Computes the effective network quality for each participant by taking
   * the minimum of their uplink and downlink quality scores. This provides
   * a conservative estimate of connection quality that reflects the worst
   * performing direction.
   *
   * @returns Record mapping Zoom user id to their effective network quality score
   */
  const derivedNetworkQuality = useMemo(() => {
    const qualities: Record<number, number> = {}
    for (const sessionParticipant of allSessionParticipants) {
      const quality = networkQuality[sessionParticipant.userId]
      if (!quality) continue
      // Use the lower of uplink/downlink as the effective quality
      qualities[sessionParticipant.userId] = Math.min(
        quality.downlink ?? Infinity,
        quality.uplink ?? Infinity,
      )
    }
    return qualities
  }, [networkQuality, allSessionParticipants])

  /**
   * Network quality change event handler
   *
   * Processes real-time network quality updates from the Zoom SDK.
   * Updates the quality tracking state and forwards events to the
   * event handling system for logging/analytics.
   *
   * @param e Network quality change event from Zoom SDK
   */
  const handleNetworkQualityChange = (e: NetworkQualityChange) => {
    // Update network quality state for the specific user and quality type
    setNetworkQuality(state => {
      const prev = state[e.userId] ?? {}
      prev[e.type] = e.level
      return { ...state, [e.userId]: prev }
    })

    // Forward event to analytics/logging system
    handleEvent({ eventName: 'network-quality-change', eventPayload: e })
  }

  /**
   * Component mount effect
   *
   * Initializes the Zoom session and sets up event listeners when the component mounts.
   * This is the entry point for starting a new video call session.
   */
  useMount(() => {
    fireAndForget('[Zoom] initStream', handleInitStream)
    client.on('network-quality-change', handleNetworkQualityChange)
  })

  /**
   * Component unmount effect
   *
   * Performs cleanup when the component unmounts to prevent memory leaks
   * and ensure proper session termination. Removes event listeners and
   * cleans up all Zoom resources.
   */
  useUnmount(() => {
    void handleCleanup()
    client.off('network-quality-change', handleNetworkQualityChange)
  })

  /**
   * Conditional rendering of the video call interface
   *
   * Only renders the SessionVideoContainer when all required conditions are met:
   * - Current user is properly initialized with a valid userId
   * - Media stream is available
   * - Session initialization is complete
   *
   * Returns null during initialization to prevent rendering with incomplete state.
   *
   * @returns SessionVideoContainer component or null if not ready
   */
  return currentUser?.userId && stream && isInitialized ? (
    <SessionVideoContainer
      currentUser={currentUser}
      setCurrentUser={setCurrentUser}
      client={client}
      stream={stream}
      setIsVideoOn={setIsVideoOn}
      isVideoOn={isVideoOn}
      allSessionParticipants={allSessionParticipants}
      handleLeaveCall={handleLeaveCall}
      derivedNetworkQuality={derivedNetworkQuality}
      // The provider's own effective quality, read from the same per-user map the tiles use so the
      // self indicator can never disagree with the provider's own tile badge.
      selfNetworkQuality={derivedNetworkQuality[currentUser.userId]}
    />
  ) : null
}
